import { useId } from '@react-aria/utils'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import React from 'react'
import { Loader } from '../Loader/Loader'
import { loaderTransitionDuration } from '../Loader/Loader.styles'
import { useI18nContext } from '../_hooks/I18n'
import {
  pulseAnimationDuration,
  StyledErrorMessage,
  StyledFileToken,
  StyledFileTokenErrorContainer,
} from './FileToken.styles'
import type { FileTokenProps } from './FileToken.types'
import { FileTokenInner } from './FileTokenInner'

/**
 * @deprecatedSince 11
 * @deprecated Intended for internal library development.
 */
export const FileToken = React.forwardRef<
  HTMLDivElement,
  FileTokenProps & React.HTMLAttributes<HTMLDivElement>
>(function FileToken(
  {
    fileName,
    progressValue = 0,
    errorMessage = '',
    onLabelClick,
    onClose,
    qa,
    ...props
  },
  ref
) {
  const i18n = useI18nContext()
  const isError = Boolean(errorMessage)

  const isProgressValueComplete = !isError && progressValue >= 100

  const [isProgressComplete, setProgressComplete] = React.useState(
    isProgressValueComplete
  )

  const [isFinalized, setFinalized] = React.useState(false)

  const didMountRef = React.useRef(false)

  const progressBarLabelId = useId()

  React.useEffect(() => {
    const timerId: number | null = isProgressValueComplete
      ? window.setTimeout(() => {
          setProgressComplete(true)
        }, loaderTransitionDuration)
      : null

    if (!isProgressValueComplete) {
      setProgressComplete(false)
    }

    return () => {
      timerId && clearTimeout(timerId)
    }
  }, [isProgressValueComplete])

  React.useEffect(() => {
    const isFinalized = didMountRef.current && isProgressComplete
    const timerId: number | null = isFinalized
      ? window.setTimeout(() => {
          setFinalized(false)
        }, pulseAnimationDuration)
      : null

    setFinalized(didMountRef.current && isProgressComplete)

    if (!didMountRef.current) {
      didMountRef.current = true
    }

    return () => {
      timerId && clearTimeout(timerId)
    }
  }, [isProgressComplete])

  const renderToken = (includeInTabOrder = false) => (
    <FileTokenInner
      fullFilename={fileName}
      includeInTabOrder={includeInTabOrder}
      onClick={onLabelClick}
      onClose={onClose}
      qaCloseBtn={qa?.closeButton}
    />
  )

  if (isError) {
    return (
      <StyledFileTokenErrorContainer {...props} ref={ref}>
        <StyledFileToken
          $error={isError}
          $finalized={isFinalized}
          $progressComplete={isProgressComplete}
        >
          {renderToken(true)}
          <Loader value={progressValue} animated>
            {renderToken()}
          </Loader>
        </StyledFileToken>
        <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
      </StyledFileTokenErrorContainer>
    )
  }

  return (
    <StyledFileToken
      {...props}
      $error={isError}
      $finalized={isFinalized}
      $progressComplete={isProgressComplete}
      ref={ref}
    >
      {renderToken(true)}
      <Loader value={progressValue} animated aria-hidden>
        {renderToken()}
      </Loader>
      <VisuallyHidden>
        <span id={progressBarLabelId}>
          {i18n.t('core.fileToken.uploading')}
        </span>
        <div
          aria-labelledby={progressBarLabelId}
          role="progressbar"
          aria-valuenow={progressValue}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={`${progressValue}%`}
        />
      </VisuallyHidden>
    </StyledFileToken>
  )
})

FileToken.displayName = 'FileToken'
